import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  Save,
  ShieldCheck,
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { AdminSetting } from '../types';
import { useToast } from '../contexts/ToastContext';
import {
  formatSettingUpdatedAt,
  getApiErrorMessage,
  parsePlatformSettingValue,
  PLATFORM_SETTING_DEFINITIONS,
  PLATFORM_UNAVAILABLE_DEFINITIONS,
  PlatformSettingDefinition,
} from '../utils/platformSettings';

interface LastSave {
  key: string;
  label: string;
  value: string;
  updatedAt: string | null;
  updatedBy: string | null;
  auditUnavailable: boolean;
}

const hasAuditMetadata = (setting: AdminSetting | undefined): boolean => {
  if (!setting?.updated_by?.trim() || !setting.updated_at) return false;
  return !Number.isNaN(new Date(setting.updated_at).getTime());
};

const PlatformSettingsPanel: React.FC = () => {
  const toast = useToast();
  const [settings, setSettings] = useState<Record<string, AdminSetting>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [lastSave, setLastSave] = useState<LastSave | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminService.getAdminSettings();
      const nextSettings: Record<string, AdminSetting> = {};
      const nextDrafts: Record<string, string> = {};

      data.forEach(setting => {
        if (!PLATFORM_SETTING_DEFINITIONS.some(definition => definition.key === setting.key)) {
          return;
        }
        nextSettings[setting.key] = setting;
        nextDrafts[setting.key] = setting.value;
      });

      setSettings(nextSettings);
      setDrafts(nextDrafts);
      setFieldErrors({});
      setLastSave(previous => {
        if (!previous?.auditUnavailable) return previous;
        return hasAuditMetadata(nextSettings[previous.key]) ? null : previous;
      });
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Unable to load the platform settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleDraftChange = (definition: PlatformSettingDefinition, rawValue: string) => {
    setDrafts(previous => ({ ...previous, [definition.key]: rawValue }));
    setFieldErrors(previous => {
      if (!previous[definition.key]) return previous;
      const next = { ...previous };
      delete next[definition.key];
      return next;
    });
  };

  const handleSaveSetting = async (definition: PlatformSettingDefinition) => {
    const setting = settings[definition.key];
    if (!setting || savingKey !== null) return;

    const rawValue = drafts[definition.key] ?? setting.value;
    const result = parsePlatformSettingValue(definition, rawValue);
    if (!result.parsed) {
      setFieldErrors(previous => ({
        ...previous,
        [definition.key]: result.error ?? `Enter a valid value for ${definition.label}.`,
      }));
      return;
    }

    const confirmed = window.confirm(
      `Save ${definition.label} as ${result.parsed.canonicalValue} ${definition.unit}? ` +
        'This changes the value used by new withdrawal requests.'
    );
    if (!confirmed) return;

    setFieldErrors(previous => {
      const next = { ...previous };
      delete next[definition.key];
      return next;
    });
    setSavingKey(definition.key);

    try {
      const savedSetting = await adminService.updateAdminSetting(
        definition.key,
        result.parsed.value
      );
      const auditUnavailable = !hasAuditMetadata(savedSetting);

      setSettings(previous => ({ ...previous, [definition.key]: savedSetting }));
      setDrafts(previous => ({
        ...previous,
        [definition.key]: savedSetting.value,
      }));
      setLastSave({
        key: definition.key,
        label: definition.label,
        value: savedSetting.value,
        updatedAt: savedSetting.updated_at,
        updatedBy: savedSetting.updated_by,
        auditUnavailable,
      });

      if (auditUnavailable) {
        toast.success(
          'Setting saved',
          `${definition.label} was saved. Refresh to retrieve its audit timestamp and actor.`
        );
      } else {
        toast.success(
          'Setting saved',
          `Audit recorded for ${savedSetting.updated_by} at ${formatSettingUpdatedAt(savedSetting.updated_at)}.`
        );
      }
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        `Unable to save ${definition.label.toLowerCase()}.`
      );
      setFieldErrors(previous => ({ ...previous, [definition.key]: message }));
      toast.error('Unable to save setting', message);
    } finally {
      setSavingKey(null);
    }
  };

  const renderUnavailableSetting = (definition: PlatformSettingDefinition) => (
    <div
      className="economy-setting-row economy-setting-row-unavailable"
      key={definition.key}
      data-testid={`platform-unavailable-${definition.key}`}
      role="status"
    >
      <div className="economy-setting-copy">
        <div className="economy-setting-heading">
          <h4>{definition.label}</h4>
          <span className="economy-setting-key">{definition.key}</span>
        </div>
        <p>{definition.description}</p>
      </div>
      <span className="economy-setting-unavailable">
        <AlertTriangle size={16} />
        Unavailable from API
      </span>
    </div>
  );

  const renderSetting = (definition: PlatformSettingDefinition) => {
    const setting = settings[definition.key];
    if (!setting) return renderUnavailableSetting(definition);

    const draftValue = drafts[definition.key] ?? setting.value;
    const parsedDraft = parsePlatformSettingValue(definition, draftValue);
    const savedValue = parsePlatformSettingValue(definition, setting.value).parsed?.canonicalValue;
    const isDirty = parsedDraft.parsed
      ? parsedDraft.parsed.canonicalValue !== (savedValue ?? setting.value)
      : draftValue !== setting.value;
    const fieldError = fieldErrors[definition.key];
    const isSaving = savingKey === definition.key;
    const isAnotherSaveRunning = savingKey !== null && !isSaving;

    return (
      <div
        className="economy-setting-row"
        key={definition.key}
        data-testid={`platform-setting-${definition.key}`}
      >
        <div className="economy-setting-copy">
          <div className="economy-setting-heading">
            <h4>{definition.label}</h4>
            <span className="economy-setting-key">{definition.key}</span>
          </div>
          <p>{setting.description || definition.description}</p>
          <div className="economy-setting-meta">
            <span>Last saved: {formatSettingUpdatedAt(setting.updated_at)}</span>
            <span>
              {setting.updated_by ? `Updated by ${setting.updated_by}` : 'Updated by unavailable'}
            </span>
          </div>
        </div>

        <div className="economy-setting-control">
          <div className="economy-setting-input-row">
            <input
              id={`platform-${definition.key}`}
              aria-label={definition.label}
              aria-invalid={fieldError ? 'true' : 'false'}
              aria-describedby={fieldError ? `platform-${definition.key}-error` : undefined}
              type="text"
              inputMode="decimal"
              step={definition.step}
              min={definition.min}
              max={definition.max}
              value={draftValue}
              disabled={isSaving || isAnotherSaveRunning}
              onChange={event => handleDraftChange(definition, event.target.value)}
            />
            <span className="economy-setting-unit">{definition.unit}</span>
          </div>
          <button
            type="button"
            className="save-btn compact economy-save-btn"
            aria-label={`Save ${definition.label}`}
            onClick={() => void handleSaveSetting(definition)}
            disabled={!isDirty || savingKey !== null}
          >
            {isSaving ? <RefreshCw className="spin" size={14} /> : <Save size={14} />}
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          {fieldError && (
            <p
              className="economy-setting-error"
              id={`platform-${definition.key}-error`}
              role="alert"
            >
              <AlertTriangle size={14} /> {fieldError}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="economy-settings-panel platform-settings-panel" data-testid="platform-settings-panel">
      <div className="economy-settings-intro">
        <div>
          <h3>Platform Settings</h3>
          <p>
            These controls are backed by the server and affect new withdrawal requests.
            Every change is validated and recorded against your admin account.
          </p>
        </div>
        <div className="economy-settings-intro-actions">
          <button
            type="button"
            className="refresh-btn"
            onClick={() => void loadSettings()}
            disabled={loading || savingKey !== null}
          >
            <RefreshCw className={loading ? 'spin' : ''} size={16} />
            Refresh
          </button>
          <ShieldCheck size={28} aria-hidden="true" />
        </div>
      </div>

      <div className="economy-settings-contract" role="note">
        <Info size={16} />
        <span>
          The fee and minimums are applied by the backend. Existing completed withdrawals keep
          their recorded values.
        </span>
      </div>

      {lastSave && (
        <div
          className={`economy-settings-confirmation ${lastSave.auditUnavailable ? 'warning' : ''}`}
          role="status"
          data-testid="platform-save-confirmation"
        >
          {lastSave.auditUnavailable ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          <div>
            <strong>{lastSave.label} saved as {lastSave.value}.</strong>
            {lastSave.auditUnavailable ? (
              <span> Refresh to retrieve its audit timestamp and actor.</span>
            ) : (
              <span>
                {' '}Audit recorded for {lastSave.updatedBy} at {formatSettingUpdatedAt(lastSave.updatedAt)}.
              </span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state" data-testid="platform-settings-loading">
          <RefreshCw className="spin" size={32} />
          <span>Loading platform settings…</span>
        </div>
      ) : loadError ? (
        <div className="economy-settings-load-error" role="alert">
          <AlertTriangle size={18} />
          <span>{loadError}</span>
          <button type="button" className="refresh-btn" onClick={() => void loadSettings()}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      ) : (
        <section className="economy-settings-group" aria-labelledby="platform-live-settings-heading">
          <h4 className="settings-group-title" id="platform-live-settings-heading">
            Live platform controls
          </h4>
          <div className="economy-settings-list">
            {PLATFORM_SETTING_DEFINITIONS.map(renderSetting)}
          </div>
        </section>
      )}

      <section className="economy-settings-group" aria-labelledby="platform-unavailable-heading">
        <h4 className="settings-group-title" id="platform-unavailable-heading">
          Not available yet
        </h4>
        <div className="economy-settings-list">
          {PLATFORM_UNAVAILABLE_DEFINITIONS.map(definition => (
            <div
              className="economy-setting-row economy-setting-row-unavailable"
              key={definition.key}
              data-testid={`platform-placeholder-${definition.key}`}
              role="status"
            >
              <div className="economy-setting-copy">
                <div className="economy-setting-heading">
                  <h4>{definition.label}</h4>
                  <span className="economy-setting-key">{definition.key}</span>
                </div>
                <p>{definition.reason}</p>
              </div>
              <span className="economy-setting-unavailable">
                <AlertTriangle size={16} />
                Unavailable
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PlatformSettingsPanel;
